import { useLingui } from '@lingui/react/macro';
import { Button, Dialog } from '@utima/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type ImageLightboxProps = {
  images: string[];
  /**
   * Positional, not per-image metadata: `annotations[i]` describes
   * `images[i]`, mirroring how `ShopProductEnrichmentService#collectImages`
   * builds the same ordered list it hands the model for annotation.
   */
  annotations?: string[] | null;
  index: number;
  alt?: string;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};

export function ImageLightbox({
  images,
  annotations,
  index,
  alt,
  onIndexChange,
  onClose,
}: ImageLightboxProps) {
  const { t } = useLingui();
  const canNavigate = images.length > 1;
  const currentAnnotation = annotations?.[index];

  const goPrev = () =>
    onIndexChange((index - 1 + images.length) % images.length);
  const goNext = () => onIndexChange((index + 1) % images.length);

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Content className='flex w-full max-w-3xl flex-col gap-3 overflow-hidden p-4'>
        <div className='relative flex items-center justify-center'>
          {canNavigate && (
            <Button
              variant='ghost'
              outline
              size='icon-sm'
              className='absolute left-1 z-10'
              onClick={goPrev}
              aria-label={t`Previous image`}
            >
              <ChevronLeft className='size-5' />
            </Button>
          )}

          <img
            src={images[index]}
            alt={alt}
            className='max-h-[65vh] w-auto max-w-full rounded-md object-contain'
          />

          {canNavigate && (
            <Button
              variant='ghost'
              outline
              size='icon-sm'
              className='absolute right-1 z-10'
              onClick={goNext}
              aria-label={t`Next image`}
            >
              <ChevronRight className='size-5' />
            </Button>
          )}
        </div>

        <span className='text-center text-xs text-muted-fg'>
          {t`Image ${index + 1} of ${images.length}`}
        </span>

        {currentAnnotation && (
          <p className='whitespace-pre-wrap text-center text-sm text-muted-fg'>
            {currentAnnotation}
          </p>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
